import { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import SpendingBreakdownCard from "../../../../../components/ui/cards/finances/SpendingBreakdownCard";
import DataTable from "../../../../../components/ui/table/DataTable";
import { useContentHeightCalculation } from "../../../../../hooks/useContentHeightCalculation";
import SpendingTransactionModal from "../../../../../components/ui/popup/finances/SpendingTransactionsModal";
import SpendingDecisionReviewModal from "../../../../../components/ui/popup/finances/SpendingDecisionReviewModal";
import {
  getSpendingHeavyHitters,
  getUnmarkedSpendingTransactions,
} from "../../../../../api/finances/spendingsAPI";
import GhostCardContainer from "../../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../../../components/ui/loading/skeletons/GhostDataTable";
import GhostChart from "../../../../../components/ui/loading/skeletons/GhostChart";
import GhostBar from "../../../../../components/ui/loading/skeletons/GhostBar";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";

import BaseChartCarousel from "../../../../../components/ui/carousels/common/BaseChartCarousel";
import StreamlineSpendingChart from "../../../../../components/ui/charts/finances/StreamlineSpendingChart";
import { useSpendingUsage } from "../../../../../hooks/finances/useSpendingUsage";
import { useBalanceUsage } from "../../../../../hooks/finances/useBalanceUsage";
import InstitutionDataLoaderBanner from "./common/InsitutionDataLoaderBanner";
import InstitutionPartialDataWarningBanner from "./common/InstitutionPartialDataWarningBanner";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";
import AlertBanner from "../../../../../components/ui/alerts/AlertBanner";

const createInitialHeavyHittersData = () => ({ period: "", heavy_hitters: [] });

function PersonalSpendings() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const { contentRef, mainRef } = useContentHeightCalculation();

  // ============================================================
  // STATE DEFINITIONS
  // ============================================================
  const [unmarkedSpendingData, setUnmarkedSpendingData] = useState({
    has_unmarked: false,
    count: 0,
    transactions: [],
  });
  const [loadingUnmarked, setLoadingUnmarked] = useState(true);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingHeavyHitters, setLoadingHeavyHitters] = useState(true);
  const [heavyHittersError, setHeavyHittersError] = useState("");
  const [heavyHittersData, setHeavyHittersData] = useState(
    createInitialHeavyHittersData(),
  );

  // ============================================================
  // PIPELINE SYNC STATE EXTRACTION & CONDITION EVALUATIONS
  // ============================================================
  const {
    institutionSyncStates = [],
    loadSyncStates,
    isLoadingSyncStates,
  } = useOutletContext() || {};

  const isEmptyOnboarding = useMemo(() => {
    if (isLoadingSyncStates) return true;
    if (institutionSyncStates.length === 0) return false;
    return institutionSyncStates.every(
      (inst) => inst.transaction_first_load_completed === false,
    );
  }, [institutionSyncStates, isLoadingSyncStates]);

  const isPartialSyncing = useMemo(() => {
    if (
      isLoadingSyncStates ||
      isEmptyOnboarding ||
      institutionSyncStates.length === 0
    )
      return false;
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === false,
    );
  }, [institutionSyncStates, isEmptyOnboarding, isLoadingSyncStates]);

  const processingInstitutionsText = useMemo(() => {
    const pendingNames = institutionSyncStates
      .filter((inst) => inst.transaction_history_completed === false)
      .map((inst) => inst.institution_name)
      .filter(Boolean);

    if (pendingNames.length === 0) return "";
    if (pendingNames.length === 1) return pendingNames[0];
    if (pendingNames.length === 2)
      return `${pendingNames[0]} & ${pendingNames[1]}`;
    return `${pendingNames.slice(0, -1).join(", ")}, & ${
      pendingNames[pendingNames.length - 1]
    }`;
  }, [institutionSyncStates]);

  const isGraphDataReady = useMemo(() => {
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === true,
    );
  }, [institutionSyncStates]);

  // ============================================================
  // USAGE HOOKS
  // ============================================================
  const spendingUsage = useSpendingUsage(isGraphDataReady);
  const balanceUsage = useBalanceUsage(null, isGraphDataReady);

  // ============================================================
  // LIVE DATA LOADING FUNCTIONS
  // ============================================================
  const loadUnmarkedTransactions = useCallback(async () => {
    setLoadingUnmarked(true);
    try {
      const res = await getUnmarkedSpendingTransactions();
      setUnmarkedSpendingData({
        has_unmarked: res?.has_unmarked || false,
        count: res?.count || 0,
        transactions: res?.transactions || [],
      });
    } catch (err) {
      console.error("Failed to sync unmarked transactions", err);
      setUnmarkedSpendingData({
        has_unmarked: false,
        count: 0,
        transactions: [],
      });
    } finally {
      setLoadingUnmarked(false);
    }
  }, []);

  const loadHeavyHitters = useCallback(async () => {
    setLoadingHeavyHitters(true);
    setHeavyHittersError("");
    try {
      const data = await getSpendingHeavyHitters();
      setHeavyHittersData(data || createInitialHeavyHittersData());
    } catch (err) {
      setHeavyHittersData(createInitialHeavyHittersData());
      setHeavyHittersError(
        "We could not load your heavy hitters. Please try again.",
      );
    } finally {
      setLoadingHeavyHitters(false);
    }
  }, []);

  // ============================================================
  // SIDE EFFECTS & LIFECYCLES
  // ============================================================
  useEffect(() => {
    if (isGraphDataReady && !isLoadingSyncStates) {
      loadUnmarkedTransactions();
      spendingUsage.refresh();
      balanceUsage.refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    institutionSyncStates,
    isGraphDataReady,
    isLoadingSyncStates,
    loadUnmarkedTransactions,
  ]);

  useEffect(() => {
    loadHeavyHitters();
  }, [loadHeavyHitters]);

  useRefreshOnAuth(() => {
    if (isGraphDataReady) {
      loadUnmarkedTransactions();
      spendingUsage.refresh();
      balanceUsage.refresh();
    }
    loadHeavyHitters();
  });

  // ============================================================
  // DATA PROCESSING VARIABLES
  // ============================================================
  const heavyHittersSourceRows = heavyHittersData.heavy_hitters || [];
  const heavyHittersLimit =
    heavyHittersData.limit ||
    heavyHittersSourceRows.reduce(
      (acc, row) => acc + Number(row.spent || 0),
      0,
    );

  const heavyHittersHeaders = [
    {
      label: content.spendings.heavy_hitters.headers.category,
      key: "category",
      type: "category",
      width: "200px",
    },
    {
      label: content.spendings.heavy_hitters.headers.spent,
      key: "spentlimit",
      width: "450px",
      variant: "heavy_hitters",
    },
  ];

  const heavyHittersDescription = heavyHittersData.period
    ? content.spendings.heavy_hitters.description_with_period.replace(
        "{period}",
        heavyHittersData.period,
      )
    : content.spendings.heavy_hitters.description;

  // VISUAL LOGIC LAYOUT SENTINEL
  const isLayoutLoading = isGraphDataReady
    ? loadingHeavyHitters ||
      spendingUsage.isLoading ||
      balanceUsage.isLoading ||
      loadingUnmarked
    : loadingHeavyHitters || loadingUnmarked;

  if (isEmptyOnboarding) {
    return (
      <InstitutionDataLoaderBanner
        content={content.status.transactions.no_data}
        refreshContent={content.global.refresh_in}
        onRefresh={loadSyncStates}
      />
    );
  }

  return (
    <Fragment>
      {isPartialSyncing && (
        <InstitutionPartialDataWarningBanner
          onRefresh={loadSyncStates}
          content={content.status.transactions.processing}
          processingText={processingInstitutionsText}
          refreshText={content.global.refresh}
        />
      )}
      <LoadingToRealTransitionWrapper
        isLoading={isLayoutLoading}
        ghost={
          <Fragment>
            {isGraphDataReady && (
              <GhostCardContainer>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <GhostBar width="120px" height="24px" />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <GhostBar width="28px" height="28px" circle />
                    <GhostBar width="28px" height="28px" circle />
                  </div>
                </div>
                <div
                  style={{ display: "flex", gap: "16px", overflow: "hidden" }}
                >
                  <div style={{ width: "85%", flexShrink: 0 }}>
                    <GhostChart />
                  </div>
                  <div style={{ width: "85%", flexShrink: 0 }}>
                    <GhostChart />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "6px",
                    marginTop: "16px",
                  }}
                >
                  <GhostBar
                    width="14px"
                    height="6px"
                    style={{ borderRadius: "9999px" }}
                  />
                  <GhostBar width="6px" height="6px" circle />
                </div>
              </GhostCardContainer>
            )}
            <GhostCardContainer>
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  justifyContent: "space-around",
                }}
              >
                <div
                  style={{
                    height: "180px",
                    width: "180px",
                    borderRadius: "50%",
                    border: "15px solid #f3f4f6",
                  }}
                />
                <div
                  style={{
                    height: "180px",
                    width: "180px",
                    borderRadius: "50%",
                    border: "15px solid #f3f4f6",
                  }}
                />
              </div>
            </GhostCardContainer>
            <GhostCardContainer>
              <GhostDataTable
                title={content.spendings.heavy_hitters.title}
                description={content.spendings.heavy_hitters.description}
                headers={heavyHittersHeaders}
                rows={3}
                showAddButton={false}
              />
            </GhostCardContainer>
          </Fragment>
        }
      >
        {unmarkedSpendingData.has_unmarked && (
          <AlertBanner
            title={content.spendings.review_required.title}
            message={content.spendings.review_required.message.replace(
              "{count}",
              unmarkedSpendingData.count,
            )}
            variant="warning"
            actionButton={
              <button
                onClick={() => setIsDecisionModalOpen(true)}
                className="cta-button primary small rounded"
              >
                {content.spendings.review_required.action_text}
              </button>
            }
            style={{ marginBottom: "24px" }}
          />
        )}

        {isGraphDataReady && (
          <div className="standard-card-container mobile-reset-standard-card-container">
            <StreamlineSpendingChart
              data={spendingUsage}
              title={content.spendings.spending_balance.title}
              growthCopy={content.spendings.spending_balance.growth}
              unstyled={true}
            />
          </div>
        )}
        <div className="mobile-container-separator"></div>
        <SpendingBreakdownCard />
        <div className="mobile-container-separator"></div>
        {heavyHittersError ? (
          <div style={{ marginTop: "24px", padding: "10px", width: "100%" }}>
            <h4 className="margin-reset">
              {content.spendings.heavy_hitters.title}
            </h4>
            <span style={{ fontSize: "14px", color: "#6B7280" }}>
              {heavyHittersError}
            </span>
          </div>
        ) : (
          <DataTable
            title={content.spendings.heavy_hitters.title}
            description={heavyHittersDescription}
            headers={heavyHittersHeaders}
            rows={heavyHittersSourceRows}
            rowDefaults={{ limit: heavyHittersLimit }}
            emptyStateTitle={content.spendings.heavy_hitters.empty}
            emptyStateText={content.spendings.heavy_hitters.empty}
            copy={content.spendings.heavy_hitters.spent_tooltips}
            getActionMenu={(row) => [
              {
                label: content.spendings.heavy_hitters.actions.see_transactions,
                onClick: () => {
                  setSelectedCategory(row);
                  setIsModalOpen(true);
                },
              },
            ]}
          />
        )}
      </LoadingToRealTransitionWrapper>

      {isModalOpen && selectedCategory && (
        <SpendingTransactionModal
          title={content.spendings.heavy_hitters.modal_title
            .replace("{period}", heavyHittersData.period)
            .replace("{category}", selectedCategory.category)}
          category={selectedCategory.category}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {isDecisionModalOpen && (
        <SpendingDecisionReviewModal
          title={content.spendings.review_required.title}
          transactions={unmarkedSpendingData.transactions}
          onClose={() => setIsDecisionModalOpen(false)}
          onSuccess={async () => {
            await loadUnmarkedTransactions();
            spendingUsage.refresh();
            await loadHeavyHitters();
          }}
        />
      )}
    </Fragment>
  );
}

export default PersonalSpendings;
